'use client'

import Link from 'next/link'
import Image from 'next/image'
import { motion } from 'framer-motion'

type ProductProps = {
  number: string
  title: string
  subtitle: string
  description: string
  buttonText: string
  buttonLink: string
  imageSrc: string
  index?: number
}

const ProductCard = ({
  number,
  title,
  subtitle,
  description,
  buttonText,
  buttonLink,
  imageSrc,
  index = 0,
}: ProductProps) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      viewport={{ once: true }}
      whileHover={{ y: -5 }}
      className="relative flex h-full flex-col overflow-hidden rounded-xl bg-white shadow-lg transition-transform duration-300 hover:shadow-xl"
    >
      {/* Product Image */}
      <div className="relative h-64 w-full overflow-hidden">
        <Image
          src={imageSrc}
          alt={title}
          fill
          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
          style={{ objectFit: 'cover' }}
          priority={index === 0}
          className="transition-transform duration-300 hover:scale-105"
        />
        {/* Number badge */}
        <div className="absolute top-6 left-6 flex h-14 w-14 items-center justify-center rounded-full bg-[#ff4500] text-xl font-bold text-white shadow-md">
          {number}
        </div>
      </div>

      <div className="flex flex-1 flex-col p-6">
        <div className="flex-1">
          <h3 className="mb-2 text-2xl font-bold hyphens-auto whitespace-normal">{title}</h3>
          <p className="mb-4 text-xl font-bold text-[#ff4500]">{subtitle}</p>
          <p className="text-gray-600 dark:text-gray-300">{description}</p>
        </div>

        <div className="mt-6 flex justify-center md:justify-start">
          <Link
            href={buttonLink}
            className="rounded-lg bg-[#ff4500] px-6 py-3 font-medium text-white transition-colors duration-200 hover:bg-[#e03e00]"
          >
            {buttonText}
          </Link>
        </div>
      </div>
    </motion.div>
  )
}

export default function ProductsSection() {
  return (
    <section className="py-24 md:px-0">
      <div className="mx-auto max-w-[1440px]">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="mb-12 text-center"
        >
          <h2 className="mb-4 text-3xl font-bold sm:text-4xl md:text-5xl">
            Die Volks-Energielösung
          </h2>
          <p className="mx-auto max-w-2xl text-lg text-gray-600 dark:text-gray-300">
            Energieeffizienz zu erschwinglichen Preisen für jeden Haushalt
          </p>
        </motion.div>

        <div className="grid auto-rows-fr grid-cols-1 gap-6 text-center sm:grid-cols-2 md:grid-cols-1 md:text-left lg:grid-cols-3 xl:grid-cols-3">
          <ProductCard
            number="1"
            title="Die Volks-Photovoltaik-Anlage schon ab&nbsp;12.900€"
            subtitle=""
            description="Wir bieten jede Größe zum günstigen Preis. Unsere Photovoltaik-Komplettsysteme bestehen aus Glas Glas Black Frame Modulen mit 450 Wp für eine optimale Stromerzeugung und einem Lithium Eisenphosphat Batteriespeicher für die optimale Sicherheit und eine hohe Autarkie. Unser leistungsstarker Wechselrichter von Deya ist per App bedienbar und sorgt für einen störungsfreien Betrieb. Unsere Angebote beinhalten die bundesweite Installation und die Inbetriebnahme."
            buttonText="Zur Volks-Photovoltaik"
            buttonLink="/photovoltaik"
            imageSrc="/images/solar-panel.webp"
            index={0}
          />

          <ProductCard
            number="2"
            title="Die Volks-Isolierung 100&nbsp;qm² Außenfassade für nur 9.000€"
            subtitle=""
            description="Das Low Emission Coating ist ein 2 in 1 Produkt, das in Deutschland entwickelt worden ist und bereits weltweit erfolgreich angewendet wird. Unsere Volks-Isolierung ersetzt einen Farbanstrich und hält gleichzeitig Innenräume warm oder angenehm kalt – je nach Bedarf. Die Energiekosten können so um bis zu 50% reduziert werden und die Fassaden bleiben langfristig trocken und sauber. Der Effekt tritt dadurch ein, dass Kälte- und Wärmestrahlung reflektiert werden."
            buttonText="Zur Volks-Isolierung"
            buttonLink="/isolierung"
            imageSrc="/images/isolierung1.webp"
            index={1}
          />

          <ProductCard
            number="3"
            title="Die Volks-Heizung schon ab&nbsp;305&nbsp;€"
            subtitle=""
            description="Mit unserem Partner, der infraStar GmbH, können wir das weltweit größte Sortiment an Infrarotheizungen anbieten. Eine neue Generation des Heizens, wirtschaftlich in der Anschaffung und im Verbrauch, ohne Wartungsaufwand und in Kombination mit einer PV-Anlage sogar fast autonom. In Verbindung mit unserer Volks-Isolierung wird die Heizwirkung  zusätzlich verbessert und es kann ein hoher Autarkie-Grad erreicht werden, bei geringen Anschaffungskosten."
            buttonText="Zur Volks-Heizung"
            buttonLink="/heizung"
            imageSrc="/images/infrared-heater.webp"
            index={2}
          />
        </div>
      </div>
    </section>
  )
}
